import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Box,
  Chip,
  TextField,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { Refresh, DateRange, ErrorOutline } from '@material-ui/icons';
import UILogo from '../../UI/Logo/UILogo';
import { PERIOD_PRESETS } from './apiConfig';
import { useDashboardData } from './useDashboardData';
import { useDashboardStyles } from './styles';
import TodayPulseBanner from './TodayPulseBanner';
import OverviewKpiGrid from './OverviewKpiGrid';
import OverviewSalesCharts from './OverviewSalesCharts';
import OverviewRankingsSection from './OverviewRankingsSection';
import OverviewLiveOperations from './OverviewLiveOperations';

const formatTodayISO = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateBR = (isoDate) => {
  if (!isoDate || typeof isoDate !== 'string') return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const OverviewDashboard = () => {
  const classes = useDashboardStyles();
  const [preset, setPreset] = useState('today');
  const [customStart, setCustomStart] = useState(formatTodayISO);
  const [customEnd, setCustomEnd] = useState(formatTodayISO);

  const {
    data,
    isLoading,
    hasErrors,
    refresh,
    lastUpdated,
  } = useDashboardData({
    preset,
    startDate: customStart,
    endDate: customEnd,
  });

  const periodInfo = data?.period;

  return (
    <div
      className={`${classes.container} ${
        isLoading ? classes.containerLoading : classes.containerNormal
      }`}
    >
      {/* --- CONTAINER SUPERIOR: TÍTULO + PULSO DE HOJE (INDEPENDENTE DO FILTRO) --- */}
      <Paper
        style={{
          padding: '16px 20px',
          marginBottom: 20,
          borderRadius: 8,
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={data?.todayPulse || isLoading ? 2 : 0}
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
            <UILogo name="Visão" lastName="Geral" size={25} />
            <Typography variant="body2" style={{ color: '#64748b' }}>
              Pulso do dia, vendas, principais produtos e operação da loja em tempo real
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mt={{ xs: 1, sm: 0 }}>
            {lastUpdated && (
              <Typography
                variant="caption"
                style={{ color: '#64748b', marginRight: 12 }}
              >
                Atualizado às{' '}
                {lastUpdated.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Refresh />}
              onClick={refresh}
              disabled={isLoading}
              style={{ textTransform: 'none', fontWeight: 700, borderRadius: 8 }}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        {isLoading && !data && (
          <Skeleton
            variant="rect"
            height={150}
            style={{ borderRadius: 12 }}
          />
        )}

        {data?.todayPulse && <TodayPulseBanner todayPulse={data.todayPulse} />}
      </Paper>

      {/* --- BARRA DE FILTROS DE PERÍODO (APLICA AOS DADOS ABAIXO) --- */}
      <Paper
        style={{
          padding: '12px 20px',
          marginBottom: 20,
          backgroundColor: '#ffffff',
          borderRadius: 8,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <Box display="flex" alignItems="center" mr={1}>
              <DateRange
                fontSize="small"
                style={{ color: '#475569', marginRight: 6 }}
              />
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 700, color: '#1e293b' }}
              >
                Período de Análise:
              </Typography>
            </Box>

            {PERIOD_PRESETS.map((item) => {
              const isSelected = preset === item.id;
              return (
                <Chip
                  key={item.id}
                  label={item.label}
                  clickable
                  disabled={isLoading}
                  onClick={() => setPreset(item.id)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'default' : 'outlined'}
                  style={{
                    fontWeight: isSelected ? 700 : 500,
                  }}
                />
              );
            })}
          </Box>

          {periodInfo && preset !== 'custom' && (
            <Typography
              variant="caption"
              style={{ color: '#64748b', fontWeight: 600, marginTop: 4 }}
            >
              Exibindo:{' '}
              {periodInfo.startDate === periodInfo.endDate
                ? formatDateBR(periodInfo.startDate)
                : `${formatDateBR(periodInfo.startDate)} até ${formatDateBR(
                    periodInfo.endDate
                  )}`}
            </Typography>
          )}
        </Box>

        {preset === 'custom' && (
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            mt={2}
            pt={1.5}
            style={{ borderTop: '1px solid #e2e8f0', gap: 12 }}
          >
            <TextField
              label="Data Inicial"
              type="date"
              size="small"
              variant="outlined"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Data Final"
              type="date"
              size="small"
              variant="outlined"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}
      </Paper>

      {/* --- ESTADO DE ERRO --- */}
      {hasErrors && !isLoading && (
        <Paper
          elevation={0}
          style={{
            padding: 32,
            textAlign: 'center',
            borderRadius: 12,
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            marginBottom: 24,
          }}
        >
          <ErrorOutline
            style={{ fontSize: 44, color: '#dc2626', marginBottom: 8 }}
          />
          <Typography
            variant="h6"
            style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}
          >
            Não foi possível carregar os dados da Visão Geral
          </Typography>
          <Typography
            variant="body2"
            style={{ color: '#b91c1c', marginBottom: 16 }}
          >
            Verifique sua conexão ou tente atualizar novamente.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={refresh}
          >
            Tentar Novamente
          </Button>
        </Paper>
      )}

      {/* --- SKELETON DE CARREGAMENTO INICIAL --- */}
      {isLoading && !data && (
        <Box>
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={n}>
                <Skeleton
                  variant="rect"
                  height={130}
                  style={{ borderRadius: 10 }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton
                variant="rect"
                height={340}
                style={{ borderRadius: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton
                variant="rect"
                height={340}
                style={{ borderRadius: 10 }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* --- CONTEÚDO DO PERÍODO SELECIONADO --- */}
      {data && (
        <>
          <OverviewKpiGrid kpis={data.kpis} />

          <OverviewSalesCharts
            charts={data.charts}
            isSingleDay={Boolean(data.period?.isSingleDay)}
          />

          <OverviewRankingsSection rankings={data.rankings} />

          <OverviewLiveOperations liveOperations={data.liveOperations} />
        </>
      )}
    </div>
  );
};

export default OverviewDashboard;

